import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  Placement,
  shift,
  size,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from "@floating-ui/react";
import React, { ReactNode, RefObject, useEffect } from "react";

import { useFloatingTransition } from "../../hooks/useFloatingTransition";
import dimensionTokens from "../../tokens/dimension.value.tokens.json";
import ElevatedBox from "../ElevatedBox";
import { ComposedModal, EmptyModal } from "components/Modal";
import { useAppSelector } from "hooks/useStore";
import useLabels from "hooks/useLabels";

// Use tokens directly - values are already in px
const DROPDOWN_MIN_WIDTH = parseInt(
  dimensionTokens.component.popover["max-width"].$value
); // 350px
const DROPDOWN_OFFSET_VALUE = parseInt(dimensionTokens.spacing["xx-sm"].$value); // 4px

export interface DropdownMenuProps {
  /** Content to be displayed in the DropdownMenu */
  children: ReactNode;
  /** Ref for the trigger element */
  triggerRef: RefObject<HTMLElement | null>;
  /** Whether the DropdownMenu is open */
  open: boolean;
  /** Callback when DropdownMenu open state changes */
  onOpenChange: (open: boolean) => void;
  /** Padding for the content */
  hasPadding?: boolean;
  /** Placement of the dropdown menu */
  placement?: Placement;
  /** Title for the mobile modal */
  mobileTitle?: string;
  /** Whether to use a portal for rendering. Defaults to false */
  renderInPortal?: boolean;
}

/**
 * Renders an `ElevatedBox` with the content passed into `children` .
 */

const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  triggerRef,
  open,
  onOpenChange,
  hasPadding = true,
  placement = "bottom-start",
  mobileTitle,
  renderInPortal = false,
}) => {
  const { refs, floatingStyles, context } = useFloating({
    placement: placement,
    middleware: [
      offset(DROPDOWN_OFFSET_VALUE),
      flip(), // Flips to opposite side when there's no space
      shift({ padding: 8 }), // Shifts along the axis to stay in view
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: `${Math.max(rects.reference.width, DROPDOWN_MIN_WIDTH)}px`,
            minWidth: `${DROPDOWN_MIN_WIDTH}px`,
          });
        },
      }),
    ],
    whileElementsMounted: autoUpdate,
    open,
    onOpenChange,
  });

  useEffect(() => {
    refs.setReference(triggerRef.current);
  }, [refs, triggerRef]);

  const isMobile = useAppSelector((state) => state.setting.isMobile);

  // Only set up floating UI interactions for desktop
  const click = useClick(context, { enabled: !isMobile });
  const dismiss = useDismiss(context, { enabled: !isMobile });
  const { getFloatingProps } = useInteractions([click, dismiss]);

  const { isMounted, transitionStyles } = useFloatingTransition(context);
  const [labels] = useLabels("general");

  const floatingContent = (
    <div
      style={{
        ...floatingStyles,
        ...transitionStyles,
        zIndex: 10,
      }}
      ref={refs.setFloating}
      {...getFloatingProps()}
    >
      <ElevatedBox
        padding={hasPadding ? "base" : "none"}
        className="overflow-hidden"
      >
        {children}
      </ElevatedBox>
    </div>
  );

  return (
    <>
      {/* Desktop: Floating UI dropdown */}
      {!isMobile && isMounted && (
        <>
          {renderInPortal ? (
            <FloatingPortal>{floatingContent}</FloatingPortal>
          ) : (
            floatingContent
          )}
        </>
      )}

      {/* Mobile: Modal bottom sheet */}
      {isMobile && (
        <EmptyModal open={open} onClose={() => onOpenChange(false)}>
          <ComposedModal>
            <ComposedModal.Header
              title={mobileTitle || labels["dropdown-fallback-title"]}
              onClose={() => onOpenChange(false)}
            />
            <ComposedModal.Body className="m-0!">{children}</ComposedModal.Body>
          </ComposedModal>
        </EmptyModal>
      )}
    </>
  );
};

export default DropdownMenu;
